import { Paged } from 'domains/appDomain';
import { MeasurementUnitEnum } from 'domains/measurementDomain';
import {
  Organization,
  OrgBalancePaymentDTO,
  OrgColorsDTO,
  OrgCsrDTO,
  OrgCsrItemDTO,
  OrgShareContactDefaultParams,
  SpecialCsrItemDTO,
  SpecialSkuDTO,
  UpdateOrgTypeParam,
} from '../domains/orgDomain';
import api from '../utils/api';
import {
  Company,
  Contact,
  ContactAddress,
  CreateAddrParam,
  CreateCtParam,
  CreateOrgParam,
  ListCtParam,
  PartialCt,
  UpdateAddrParam,
  UpdateCtParam,
  UpdateOrgParam,
  UserContactAddress,
} from 'domains/orgDomain';
import { OrgSchedule, TimeframeConfig } from 'domains/tfDomain';
const orgService = {
  /**
   * The measurement system an org works in: its own setting, else its country, else imperial.
   *
   * Resolved server-side because only it can read the org row. This is what the org's data is
   * entered and stored in, and what its pages are read in - people do not pick a unit.
   */
  getDefaultMeasurementUnit: async (orgId?: number) => {
    const suffix = orgId ? `?orgId=${orgId}` : '';
    return api.get<MeasurementUnitEnum>(
      `/api/orgs/measurement-unit/default${suffix}`
    );
  },
  getOrgs: async () => {
    const resp = await api.get<Organization[]>(`/api/orgs/list/all`);

    return resp;
  },

  getOrgById: async (orgId: number) => {
    return await api.get<Organization>(`/api/orgs/${orgId}`);
  },

  updateOrg: async (org: UpdateOrgParam) => {
    console.log('org in service', org);
    console.log('Updating organization salesRep: ', org.salesRep);
    const resp = await api.put<Organization>(`/api/orgs/${org.id}`, org);
    return resp;
  },

  updateOrgType: async (updateOrgTypeParam: UpdateOrgTypeParam) => {
    console.log('org in service', updateOrgTypeParam);
    const resp = await api.post<Organization>(
      `/api/orgs/update-org-type/${updateOrgTypeParam.id}`,
      updateOrgTypeParam
    );
    return resp;
  },

  createOrg: async (org: CreateOrgParam) => {
    const resp = await api.post<Organization>(`/api/orgs`, org);
    return resp;
  },

  removeOrg: async (orgId: number) => {
    return await api.delete<void>(`/api/orgs/${orgId}`);
  },

  getCompanies: async () => {
    return await api.get<Company[]>(`/api/companies/list/all`);
  },

  createCompany: async (companyName: string) => {
    return await api.post(`/api/companies`, { name: companyName });
  },

  getContacts: async (orgId: number | string) => {
    const resp = await api.get<Contact[]>(`/api/contacts/list/all`, undefined, {
      'X-Tenant-ID': orgId,
    });
    return resp;
  },

  queryContacts: async (param: ListCtParam) => {
    const pageNumber = param.page.currentPage - 1;
    const pageSize = param.page.pageSize || 20;
    return await api.post<Paged<Contact>>(
      `/api/contacts/list?page=${pageNumber}&size=${pageSize}`,
      param.query,
      { 'X-Tenant-ID': param.orgId }
    );
  },

  updateContact: async (ct: UpdateCtParam) => {
    return await api.put<Contact>(`/api/contacts/${ct.id}`, ct, {
      'X-Tenant-ID': ct.orgId,
    });
  },

  createContact: async (ct: CreateCtParam) => {
    return await api.post<Contact>(`/api/contacts`, ct, {
      'X-Tenant-ID': ct.orgId,
    });
  },

  removeContact: async (ct: PartialCt) => {
    return await api.delete<void>(`/api/contacts/${ct.id}`, undefined, {
      'X-Tenant-ID': ct.orgId,
    });
  },

  updateAddr: async (orgId: number, addr: UpdateAddrParam) => {
    return await api.put<ContactAddress>(
      `/api/contactAddresses/${addr.id}`,
      addr,
      {
        'X-Tenant-ID': orgId,
      }
    );
  },

  createAddr: async (orgId: number, addr: CreateAddrParam) => {
    return await api.post<ContactAddress>(`/api/contactAddresses`, addr, {
      'X-Tenant-ID': orgId,
    });
  },

  removeAddr: async (addrId: number) => {
    return await api.delete<void>(`/api/contactAddresses/${addrId}`);
  },

  getTimeframeConfig: async (orgId: number) => {
    return await api.get<TimeframeConfig>(
      `/api/orgs/${orgId}/turnaround-time`,
      undefined,
      {
        'X-Tenant-ID': orgId,
      }
    );
  },

  createSchedule: async (orgId: number, schedule: Partial<OrgSchedule>) => {
    return await api.post<OrgSchedule>(
      `/api/orgs/${orgId}/turnaround-time`,
      schedule
    );
  },

  updateSchedule: async (orgId: number, schedule: Partial<OrgSchedule>) => {
    return await api.put<OrgSchedule>(
      `/api/orgs/${orgId}/turnaround-time/${schedule.id}`,
      schedule
    );
  },

  removeSchedule: async (orgId: number, scheduleId: number) => {
    return await api.delete<void>(
      `/api/orgs/${orgId}/turnaround-time/${scheduleId}`
    );
  },

  getUserContact: async (userId: number) => {
    return await api.get<UserContactAddress>(`/api/users/${userId}/contact`);
  },

  getInternalDistributionAddresses: async (orgId: number) => {
    return await api.get<string[]>(
      `/api/orgs/${orgId}/shipping/internal-distribution/addresses`
    );
  },
  updateBoxChargeEditable: async (
    boxChargeEditable: boolean,
    orgId: number
  ) => {
    return await api.put(`/api/orgs/${orgId}`, { boxChargeEditable });
  },

  updateOrgCsrDisplay: async (orgId: number, data: string) => {
    return api.put<OrgCsrDTO>(
      `/api/orgs/${orgId}/csr/revshop-csr-display-for`,
      data
    );
  },

  getOrgCsrDisplay: async (orgId: number) => {
    return api.get<OrgCsrDTO>(`/api/orgs/${orgId}/csr`);
  },

  updateOrgCsrItem: async (orgId: number, data: OrgCsrItemDTO) => {
    return api.put<OrgCsrDTO>(`/api/orgs/${orgId}/csr/org-csr-item`, data);
  },

  createOrgCsrProductItem: async (orgId: number, data: SpecialCsrItemDTO) => {
    return api.post<OrgCsrDTO>(`/api/orgs/${orgId}/csr/special-csr-item`, data);
  },

  updateOrgCsrProductItem: async (orgId: number, data: SpecialCsrItemDTO) => {
    return api.put<OrgCsrDTO>(`/api/orgs/${orgId}/csr/special-csr-item`, data);
  },

  removeOrgCsrProductItem: async (orgId: number, data: SpecialCsrItemDTO) => {
    return api.delete<OrgCsrDTO>(
      `/api/orgs/${orgId}/csr/special-csr-item`,
      data
    );
  },

  getOrgCsrContactList: async (orgId: number, keyword: string) => {
    return api.get<UserContactAddress[]>(
      `/api/orgs/${orgId}/csr/auto-complete-csrs?keyword=${keyword}`
    );
  },

  getOrgCsrSkuList: async (orgId: number, keyword: string) => {
    return api.get<SpecialSkuDTO[]>(
      `/api/orgs/${orgId}/csr/auto-complete-special-skus?keyword=${keyword}`
    );
  },
  getOrgBalancePaymentData: async (orgId: number, buyerId: string) => {
    return api.get<OrgBalancePaymentDTO>(
      `/api/orgs/${orgId}/balance-payment-data`,
      {
        buyerId,
      }
    );
  },

  getOrgColors: async (orgId: number) => {
    return api.get<OrgColorsDTO>(`/api/orgs/colors/query?orgId=${orgId}`);
  },

  saveOrgColors: async (OrgColorsDTO: OrgColorsDTO) => {
    return api.post<OrgColorsDTO>(`/api/orgs/colors/save`, OrgColorsDTO);
  },

  updateOrgShareContactDefault: async (param: OrgShareContactDefaultParams) => {
    return api.post<Organization>(`/api/orgs/update-org-share-contact-default`, param);
  },
};
export default orgService;
